import { zodResolver } from '@hookform/resolvers/zod';
import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import {
  businessLineFieldTooltips,
  businessLineMaxInterventionsOptions,
} from '@constants/businessLines.constant';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import {
  updateBusinessLineSchema,
  type UpdateBusinessLineDto,
  type UpdateBusinessLinePayload,
} from '@services/businessLines/dtos/update.dto';
import { businessLineService } from '@services/businessLines/service';
import type { UpdateBusinessLineOutput } from '@services/businessLines/types/update.type';

import { BusinessLineIconPicker } from '../@components/IconPicker';
import { BusinessLineServiceTypes } from '../@components/ServiceTypes';
import { BusinessLineDetailsLoading } from './@components/Loading';

function businessLineQueryOptions(id: string) {
  return queryOptions({
    queryKey: queryKeys.businessLines.detail(id),
    queryFn: () => businessLineService.getById(id),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute(
  '/_layout/(private)/business-lines/$id/update',
)({
  loader: ({ context, params }) => {
    const options = businessLineQueryOptions(params.id);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingComponent: BusinessLineDetailsLoading,
  pendingMs: 300,
  pendingMinMs: 600,
  component: BusinessLineUpdate,
});

function BusinessLineUpdate() {
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();
  const { queryClient } = Route.useRouteContext();
  const { data } = useSuspenseQuery(businessLineQueryOptions(id));

  const { businessLine } = data;

  const form = useForm<UpdateBusinessLineDto>({
    resolver: zodResolver(updateBusinessLineSchema),
    mode: 'onBlur',
    defaultValues: {
      translations: {
        en: businessLine.translations.en,
        fr: businessLine.translations.fr,
        nl: businessLine.translations.nl,
      },
      maxInterventions: businessLine.maxInterventions ?? -1,
      color: `#${businessLine.color}`,
      icon: businessLine.icon,
      emailBranding: businessLine.emailBranding,
      allowedServiceTypeIds:
        businessLine.allowedServiceTypes?.map(s => s.id) ?? [],
    },
  });

  const { isPending, mutate: updateBusinessLine } = useMutation<
    UpdateBusinessLineOutput,
    unknown,
    UpdateBusinessLinePayload
  >({
    mutationFn: businessLineService.update,
    onError: handleRequestError,
    onSuccess: async ({ businessLine: updated }) => {
      const detailQueryKey = queryKeys.businessLines.detail(id);
      await queryClient.cancelQueries({ queryKey: detailQueryKey });
      queryClient.setQueryData(detailQueryKey, {
        businessLine: updated,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.businessLines.all });
      navigate({ to: '..', replace: true });
      toast.success('Success', {
        description: 'Business line updated successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    updateBusinessLine({
      id,
      color: values.color,
      emailBranding: values.emailBranding,
      icon: values.icon,
      maxInterventions: values.maxInterventions,
      allowedServiceTypeIds: values.allowedServiceTypeIds,
      translations: values.translations,
    });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle hasBackButton title="Update Business Line" />

      <form
        className="mx-auto mt-5 space-y-3 p-2 sm:w-4/5 2xl:w-3/5"
        onSubmit={handleSubmit}
      >
        <div className="grid gap-2 md:grid-cols-3">
          <InputControlled
            label="Translation (EN)"
            name="translations.en"
            tooltip={businessLineFieldTooltips.translationEn}
          />
          <InputControlled
            label="Translation (FR)"
            name="translations.fr"
            tooltip={businessLineFieldTooltips.translationFr}
          />
          <InputControlled
            label="Translation (NL)"
            name="translations.nl"
            tooltip={businessLineFieldTooltips.translationNl}
          />
        </div>

        <Separator variant="form" />

        <div className="grid grid-cols-[1fr_repeat(2,0.25fr)] gap-2 lg:grid-cols-[0.4fr_repeat(2,0.15fr)_1fr]">
          <InputControlled
            label="Interventions per s. requests"
            name="maxInterventions"
            options={businessLineMaxInterventionsOptions}
            tooltip={businessLineFieldTooltips.maxInterventions}
            variant="select"
          />

          <InputControlled label="Color" name="color" type="color" />
          <BusinessLineIconPicker />
          <InputControlled
            formItemProps={{
              className:
                'col-start-1 col-end-4 lg:col-start-auto lg:col-end-auto',
            }}
            label="Email branding"
            name="emailBranding"
            tooltip={businessLineFieldTooltips.emailBranding}
          />
        </div>

        <Separator variant="form" />

        <BusinessLineServiceTypes workflowId={data.businessLine.workflowId} />

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            rounded="lg"
            size="lg"
            to=".."
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
